import React, { useState, useMemo } from 'react';
import { useTradingQuery } from '../../hooks/useTradingQuery';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { tradingService, type Trade, type TradeAggregate } from '../../services/trading';
import { BottomSheet } from '../ui/BottomSheet';
import { Dialog } from '../ui/Dialog';
import { ModeBadge } from '../ModeBadge';

/**
 * Check if the US stock market is currently open.
 * Market hours: 6:30 AM - 1:00 PM PT (9:30 AM - 4:00 PM ET), Monday-Friday
 * 
 * When market is open, we show "Today" data even if empty (no fallback to last trading day).
 * When market is closed, we fall back to the last trading day's data.
 */
function isMarketOpen(): boolean {
  const now = new Date();
  
  // Get Pacific timezone weekday separately to avoid locale formatting issues
  const dayOfWeek = now.toLocaleDateString('en-US', { 
    timeZone: 'America/Los_Angeles',
    weekday: 'short'
  }); // e.g., "Mon", "Tue", etc.
  
  // Get hours and minutes separately for reliable parsing
  const hours = parseInt(now.toLocaleString('en-US', { 
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    hour12: false
  }), 10);
  
  const minutes = parseInt(now.toLocaleString('en-US', { 
    timeZone: 'America/Los_Angeles',
    minute: 'numeric'
  }), 10);
  
  const currentMinutes = hours * 60 + minutes;
  
  // Weekend - market closed
  if (dayOfWeek === 'Sat' || dayOfWeek === 'Sun') {
    return false;
  }
  
  // Market hours: 6:30 AM - 1:00 PM PT
  const marketOpenMinutes = 6 * 60 + 30;  // 6:30 AM = 390 minutes
  const marketCloseMinutes = 13 * 60;     // 1:00 PM = 780 minutes
  
  return currentMinutes >= marketOpenMinutes && currentMinutes < marketCloseMinutes;
}

interface RecentActivityProps {
  // Optional props for backward compatibility, but component will fetch its own data
  trades?: Trade[];
  isLoading?: boolean;
}

export function RecentActivity({ trades = [], isLoading = false }: RecentActivityProps) {
  const [viewMode, setViewMode] = useState<'all' | 'paired'>('paired');
  const [selectedTrade, setSelectedTrade] = useState<TradeAggregate | null>(null);
  const [selectedOpenPosition, setSelectedOpenPosition] = useState<TradeAggregate | null>(null);

  // Fetch today's closed aggregates from server (single source of truth for P&L)
  const { data: todayClosedAggregates = [], isLoading: isLoadingClosed } = useTradingQuery({
    queryKey: ['tradeAggregates', 'today', 'closed'],
    queryFn: () => tradingService.getTodayClosedAggregates(),
  });

  // Fetch all trade aggregates for fallback (when no trades today)
  const { data: allTradeAggregates = [], isLoading: isLoadingAllAggregates } = useTradingQuery({
    queryKey: ['tradeAggregates', 'all'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeAggregates({ timeRange: 'all', limit: 10000 });
      } catch (err) {
        console.error('All aggregates fetch error:', err);
        return [];
      }
    },
  });

  // Fetch open aggregates from server (single source of truth for open positions)
  const { data: openAggregates = [], isLoading: isLoadingOpen } = useTradingQuery({
    queryKey: ['tradeAggregates', 'open'],
    queryFn: () => tradingService.getOpenAggregates(),
  });

  // Fetch trade history for "All" view (individual orders)
  const { data: allTrades = [], isLoading: isLoadingTrades } = useTradingQuery({
    queryKey: ['tradeHistory'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeHistory();
      } catch (err) {
        console.error('Trade history fetch error:', err);
        return [];
      }
    },
  });

  // Determine if we need to fall back to the last trading day
  const lastTradingDayInfo = useMemo(() => {
    const hasTodayTrades = todayClosedAggregates.length > 0;
    const marketOpen = isMarketOpen();
    
    // Don't use fallback if:
    // 1. Today has trades - show today's data
    // 2. Data is still loading - wait for it
    // 3. Market is currently open - show empty "Today" rather than stale data
    if (hasTodayTrades || allTradeAggregates.length === 0 || isLoadingClosed || marketOpen) {
      return {
        shouldFallback: false,
        lastTradingDateLabel: 'Today',
        lastTradingDayAggregates: [] as TradeAggregate[],
      };
    }

    // Find the most recent trading day from all aggregates
    const dateMap = new Map<string, { closeDate: Date; aggregates: TradeAggregate[] }>();
    
    allTradeAggregates.forEach((agg: TradeAggregate) => {
      if (agg.closeTime) {
        const closeDate = new Date(agg.closeTime);
        const dateKey = closeDate.toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
        
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { closeDate, aggregates: [] });
        }
        dateMap.get(dateKey)!.aggregates.push(agg);
      }
    });

    const sortedDates = Array.from(dateMap.keys()).sort().reverse();
    
    if (sortedDates.length === 0) {
      return {
        shouldFallback: false,
        lastTradingDateLabel: 'Today',
        lastTradingDayAggregates: [] as TradeAggregate[],
      };
    }

    const lastDateKey = sortedDates[0];
    const lastDateInfo = dateMap.get(lastDateKey)!;
    
    const dateLabel = lastDateInfo.closeDate.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    return {
      shouldFallback: true,
      lastTradingDateLabel: dateLabel,
      lastTradingDayAggregates: lastDateInfo.aggregates,
    };
  }, [todayClosedAggregates, allTradeAggregates, isLoadingClosed]);

  // Get effective closed aggregates (with fallback to last trading day)
  const effectiveClosedAggregates = useMemo(() => {
    return lastTradingDayInfo.shouldFallback 
      ? lastTradingDayInfo.lastTradingDayAggregates 
      : todayClosedAggregates;
  }, [lastTradingDayInfo, todayClosedAggregates]);

  // Filter trades for today or last trading day (for "All" view only)
  const todaysTrades = useMemo(() => {
    // Use Pacific timezone for consistency with server
    const todayKey = new Date().toLocaleDateString('en-CA', {
      timeZone: 'America/Los_Angeles'
    });

    // If falling back, get the last trading day's date key
    let targetDateKey = todayKey;
    if (lastTradingDayInfo.shouldFallback && lastTradingDayInfo.lastTradingDayAggregates.length > 0) {
      const firstAgg = lastTradingDayInfo.lastTradingDayAggregates[0];
      if (firstAgg.closeTime) {
        targetDateKey = new Date(firstAgg.closeTime).toLocaleDateString('en-CA', {
          timeZone: 'America/Los_Angeles'
        });
      }
    }

    return allTrades.filter((trade: Trade) => {
      const tradeDate = new Date(trade.executedAt || trade.createdAt);
      const tradeDateKey = tradeDate.toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles'
      });
      return tradeDateKey === targetDateKey;
    }).sort((a: Trade, b: Trade) => {
      const dateA = new Date(a.executedAt || a.createdAt);
      const dateB = new Date(b.executedAt || b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
  }, [allTrades, lastTradingDayInfo]);

  // Calculate totals from effective data
  const totalPnL = effectiveClosedAggregates.reduce((sum, agg) => sum + agg.netPnl, 0);
  const wins = effectiveClosedAggregates.filter(agg => agg.isWin === true).length;
  const losses = effectiveClosedAggregates.filter(agg => agg.isWin === false).length;

  const isActuallyLoading = isLoading || isLoadingClosed || isLoadingOpen || isLoadingTrades || isLoadingAllAggregates;

  const formatCurrency = (amount: number | string | undefined) => {
    if (amount === undefined || amount === null) return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(numAmount);
  };

  const formatTime = (timestamp: string | Date | null) => {
    if (!timestamp) return '--';
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Los_Angeles'
    });
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
      });
    }
  };

  // Trade details content component for both BottomSheet and Dialog
  const TradeDetailsContent = ({ 
    trade, 
    formatCurrency, 
    formatTime 
  }: { 
    trade: TradeAggregate; 
    formatCurrency: (amount: number | string | undefined) => string;
    formatTime: (timestamp: string | Date | null) => string;
  }) => (
    <div className="space-y-4">
      {/* P&L Header with Emoji */}
      <div className="text-center py-4">
        <div className="text-4xl mb-2">
          {trade.netPnl >= 0 ? '🤑' : '😢'}
        </div>
        <div className={`text-3xl font-bold ${
          trade.netPnl >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {trade.netPnl >= 0 ? '+' : ''}{formatCurrency(trade.netPnl)}
        </div>
        <div className={`text-sm ${
          trade.netPnl >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        }`}>
          {trade.buyNotionalTotal > 0 
            ? `${((trade.sellNotionalTotal - trade.buyNotionalTotal) / trade.buyNotionalTotal * 100).toFixed(2)}%`
            : '0%'}
        </div>
      </div>

      {/* Shares */}
      <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="text-gray-500 dark:text-gray-400">Shares</span>
        <span className="font-medium text-gray-900 dark:text-white">
          {trade.buyQtyTotal}
        </span>
      </div>

      {/* Buy Time */}
      <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="text-gray-500 dark:text-gray-400">Buy Time</span>
        <span className="font-medium text-gray-900 dark:text-white">
          {formatTime(trade.openTime)}
        </span>
      </div>

      {/* Sell Time */}
      <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="text-gray-500 dark:text-gray-400">Sell Time</span>
        <span className="font-medium text-gray-900 dark:text-white">
          {formatTime(trade.closeTime)}
        </span>
      </div>

      {/* Buy Notional */}
      <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="text-gray-500 dark:text-gray-400">Buy Total</span>
        <span className="font-medium text-gray-900 dark:text-white">
          {formatCurrency(trade.buyNotionalTotal)}
        </span>
      </div>

      {/* Sell Notional */}
      <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="text-gray-500 dark:text-gray-400">Sell Total</span>
        <span className="font-medium text-gray-900 dark:text-white">
          {formatCurrency(trade.sellNotionalTotal)}
        </span>
      </div>

      {/* Result */}
      <div className="flex justify-between items-center py-3">
        <span className="text-gray-500 dark:text-gray-400">Result</span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          trade.isWin
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
        }`}>
          {trade.isWin ? '✓ Win' : '✗ Loss'}
        </span>
      </div>
    </div>
  );

  // Open position details content component for both BottomSheet and Dialog
  const OpenPositionDetailsContent = ({
    position,
    formatCurrency,
    formatTime
  }: {
    position: TradeAggregate;
    formatCurrency: (amount: number | string | undefined) => string;
    formatTime: (timestamp: string | Date | null) => string;
  }) => {
    const remainingQty = position.buyQtyTotal - position.sellQtyTotal;
    const avgBuyPrice = position.buyQtyTotal > 0 
      ? position.buyNotionalTotal / position.buyQtyTotal 
      : 0;
    
    return (
      <div className="space-y-4">
        {/* Status Header */}
        <div className="text-center py-4">
          <div className="text-4xl mb-2">⏳</div>
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
            Open Position
          </span>
        </div>

        {/* Shares */}
        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
          <span className="text-gray-500 dark:text-gray-400">Shares Held</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {remainingQty}
          </span>
        </div>

        {/* Avg Price */}
        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
          <span className="text-gray-500 dark:text-gray-400">Avg Buy Price</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {formatCurrency(avgBuyPrice)}
          </span>
        </div>

        {/* Cost Basis */}
        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
          <span className="text-gray-500 dark:text-gray-400">Cost Basis</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {formatCurrency(position.buyNotionalTotal)}
          </span>
        </div>

        {/* Open Time */}
        <div className="flex justify-between items-center py-3">
          <span className="text-gray-500 dark:text-gray-400">Opened At</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {formatTime(position.openTime)}
          </span>
        </div>
      </div>
    );
  };

  // Calculate trade value for display
  const getTradeDisplayData = (trade: Trade) => {
    const units = parseFloat(trade.units) || 0;
    const price = parseFloat(trade.executedPrice || trade.price || '0') || 0;
    const totalValue = units * price;
    
    return {
      totalValue,
      profit: undefined,
      profitPercent: undefined,
    };
  };

  if (isActuallyLoading) {
    return (
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Today's Trades
        </h2>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const renderPairedView = () => (
    <div className="space-y-3">
      {/* Summary stats - using server-calculated data */}
      {(effectiveClosedAggregates.length > 0 || openAggregates.length > 0) && (
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg mb-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">{effectiveClosedAggregates.length}</span> closed
            {openAggregates.length > 0 && (
              <span className="ml-2">• <span className="font-medium">{openAggregates.length}</span> open</span>
            )}
          </div>
          <div className={`text-sm font-semibold ${totalPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL)}
          </div>
        </div>
      )}

      {/* Closed trades - Mobile View */}
      <div className="sm:hidden -mx-3">
        {effectiveClosedAggregates.map((agg, index) => {
          const pnlPct = agg.buyNotionalTotal > 0 
            ? ((agg.sellNotionalTotal - agg.buyNotionalTotal) / agg.buyNotionalTotal * 100)
            : 0;
          
          return (
            <button
              key={`closed-mobile-${agg.tradeKey}-${index}`}
              onClick={() => setSelectedTrade(agg)}
              className="w-full flex items-center justify-between px-3 py-3 active:bg-gray-100 dark:active:bg-gray-800/50 transition-colors text-left border-b border-gray-100 dark:border-gray-800 last:border-0"
            >
              <div className="flex items-center gap-3">
                {/* Emoji based on P&L */}
                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xl">
                  {agg.netPnl >= 0 ? '🤑' : '😢'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-gray-900 dark:text-white">
                      {agg.ticker}
                    </span>
                    <ModeBadge mode={agg.algorithm} variant="compact" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {agg.buyQtyTotal} shares
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {formatTime(agg.openTime)} → {formatTime(agg.closeTime)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`text-base font-semibold ${
                  agg.netPnl >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {agg.netPnl >= 0 ? '+' : ''}{formatCurrency(agg.netPnl)}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 dark:text-gray-600" />
              </div>
            </button>
          );
        })}
      </div>

      {/* Closed trades - Desktop View */}
      <div className="hidden sm:block space-y-3">
      {effectiveClosedAggregates.map((agg, index) => {
        const pnlPct = agg.buyNotionalTotal > 0 
          ? ((agg.sellNotionalTotal - agg.buyNotionalTotal) / agg.buyNotionalTotal * 100)
          : 0;
        
        return (
            <button
              key={`closed-desktop-${agg.tradeKey}-${index}`}
              onClick={() => setSelectedTrade(agg)}
              className="w-full py-3 px-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
          >
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-3">
                  {/* Emoji based on P&L */}
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xl">
                    {agg.netPnl >= 0 ? '🤑' : '😢'}
                  </div>
                  <div>
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-gray-900 dark:text-white">
                  {agg.ticker}
                </span>
                <ModeBadge mode={agg.algorithm} variant="compact" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {agg.buyQtyTotal} shares
                </span>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">
                {formatTime(agg.openTime)} → {formatTime(agg.closeTime)}
              </div>
            </div>
              </div>
                <div className="text-right">
              <div className={`font-semibold ${
                agg.netPnl >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                    {agg.netPnl >= 0 ? '+' : ''}{formatCurrency(agg.netPnl)}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%
              </div>
            </div>
          </div>
            </button>
        );
      })}
      </div>

      {/* Mobile Trade Details Bottom Sheet */}
      <div className="sm:hidden">
        <BottomSheet
          isOpen={selectedTrade !== null}
          onClose={() => setSelectedTrade(null)}
          title={selectedTrade?.ticker}
        >
          {selectedTrade && (
            <TradeDetailsContent trade={selectedTrade} formatCurrency={formatCurrency} formatTime={formatTime} />
          )}
        </BottomSheet>
      </div>

      {/* Desktop Trade Details Dialog */}
      <div className="hidden sm:block">
        <Dialog
          isOpen={selectedTrade !== null}
          onClose={() => setSelectedTrade(null)}
          title={`${selectedTrade?.ticker} Trade Details`}
          className="max-w-md"
        >
          {selectedTrade && (
            <TradeDetailsContent trade={selectedTrade} formatCurrency={formatCurrency} formatTime={formatTime} />
          )}
        </Dialog>
      </div>

      {/* Open positions - from trade_aggregates with status='OPEN' */}
      {openAggregates.length > 0 && (
        <>
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-4 mb-2">
            Open Positions
          </div>
          
          {/* Mobile Open Positions */}
          <div className="sm:hidden -mx-3">
            {openAggregates.map((agg, index) => {
              const remainingQty = agg.buyQtyTotal - agg.sellQtyTotal;
              
              return (
                <button
                  key={`open-mobile-${agg.tradeKey}-${index}`}
                  onClick={() => setSelectedOpenPosition(agg)}
                  className="w-full flex items-center justify-between px-3 py-3 active:bg-amber-100 dark:active:bg-amber-900/30 transition-colors text-left bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-xl">
                      ⏳
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-base font-semibold text-gray-900 dark:text-white">
                          {agg.ticker}
                        </span>
                        <ModeBadge mode={agg.algorithm} variant="compact" />
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {remainingQty} shares
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                      Open
                    </span>
                    <ChevronRight className="w-5 h-5 text-amber-400 dark:text-amber-600" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Desktop Open Positions */}
          <div className="hidden sm:block space-y-3">
          {openAggregates.map((agg, index) => {
            const remainingQty = agg.buyQtyTotal - agg.sellQtyTotal;
            const avgBuyPrice = agg.buyQtyTotal > 0 ? agg.buyNotionalTotal / agg.buyQtyTotal : 0;
            
            return (
                <button
                  key={`open-desktop-${agg.tradeKey}-${index}`}
                  onClick={() => setSelectedOpenPosition(agg)}
                  className="w-full flex items-center justify-between py-3 px-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 text-xl">
                      ⏳
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {agg.ticker}
                      </div>
                      <ModeBadge mode={agg.algorithm} variant="compact" />
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {remainingQty} shares @ {formatCurrency(avgBuyPrice)}/share
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                    Open
                  </span>
                </div>
                </button>
            );
          })}
          </div>

          {/* Mobile Open Position Details Bottom Sheet */}
          <div className="sm:hidden">
            <BottomSheet
              isOpen={selectedOpenPosition !== null}
              onClose={() => setSelectedOpenPosition(null)}
              title={selectedOpenPosition?.ticker}
            >
              {selectedOpenPosition && (
                <OpenPositionDetailsContent position={selectedOpenPosition} formatCurrency={formatCurrency} formatTime={formatTime} />
              )}
            </BottomSheet>
          </div>

          {/* Desktop Open Position Details Dialog */}
          <div className="hidden sm:block">
            <Dialog
              isOpen={selectedOpenPosition !== null}
              onClose={() => setSelectedOpenPosition(null)}
              title={`${selectedOpenPosition?.ticker} Position Details`}
              className="max-w-md"
            >
              {selectedOpenPosition && (
                <OpenPositionDetailsContent position={selectedOpenPosition} formatCurrency={formatCurrency} formatTime={formatTime} />
              )}
            </Dialog>
          </div>
        </>
      )}

      {effectiveClosedAggregates.length === 0 && openAggregates.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">No trades today</p>
        </div>
      )}
    </div>
  );

  const renderAllTradesView = () => (
    <div className="space-y-4">
      {todaysTrades.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">No trades today</p>
        </div>
      ) : (
        todaysTrades.map((trade) => {
          const displayData = getTradeDisplayData(trade);
          const timestamp = trade.executedAt || trade.createdAt;
          
          return (
            <div key={trade.id} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  trade.action === 'BUY' 
                    ? 'bg-rh-green bg-opacity-10' 
                    : 'bg-rh-red bg-opacity-10'
                }`}>
                  <span className={`text-sm font-bold ${
                    trade.action === 'BUY' ? 'text-rh-green' : 'text-rh-red'
                  }`}>
                    {trade.action === 'BUY' ? '+' : '-'}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {trade.ticker}
                    </div>
                    {/* Annie Mode badge — supersedes the legacy plain-text algorithm display. */}
                    <ModeBadge
                      mode={trade.algorithm === 'ANNIE' || trade.algorithm === 'ERIC' ? trade.algorithm : null}
                      variant="compact"
                    />
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {trade.action} {trade.units} shares @ {formatCurrency(trade.executedPrice || trade.price)}
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(displayData.totalValue)}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    {formatTimeAgo(timestamp)}
                  </span>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const hasData = effectiveClosedAggregates.length > 0 || openAggregates.length > 0 || todaysTrades.length > 0;

  // Determine the title based on whether we're showing fallback data
  const sectionTitle = lastTradingDayInfo.shouldFallback 
    ? `${lastTradingDayInfo.lastTradingDateLabel} Trades`
    : "Today's Trades";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {sectionTitle}
        </h2>
        
        {/* View Toggle */}
        {hasData && (
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('paired')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'paired'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Paired
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'all'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              All
            </button>
          </div>
        )}
      </div>
      
      {viewMode === 'paired' ? renderPairedView() : renderAllTradesView()}
      
      {/* View All Trade History Link */}
      {hasData && (
        <div className="pt-4 border-t border-gray-100 dark:border-gray-700 mt-4">
          <Link
            to="/trading"
            className="flex items-center justify-center w-full py-2 px-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            View All Trade History
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      )}
    </div>
  );
}
